import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  forgotPassword: (email, originUrl) =>
    api.post('/auth/forgot-password', { email, origin_url: originUrl }),
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),
};

export const fiscalApi = {
  closeYear: (schoolYear) => api.post('/fiscal/close-year', { school_year: schoolYear, confirm: true }),
  listArchives: () => api.get('/fiscal/archives'),
  getArchive: (schoolYear) => api.get(`/fiscal/archives/${schoolYear}`),
};

// Users
export const usersApi = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  delete: (id) => api.delete(`/users/${id}`)
};

// School
export const schoolApi = {
  getInfo: () => api.get('/school'),
  updateInfo: (data) => api.put('/school', data)
};

// Classes
export const classesApi = {
  getAll: () => api.get('/classes')
};

// Students
export const studentsApi = {
  getAll: (params) => api.get('/students', { params }),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
  import: (file, { dryRun = false, previewOnly = false, columnMapping = null, allowDuplicates = false, saveMappingAs = null } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (columnMapping) formData.append('column_mapping', JSON.stringify(columnMapping));
    if (saveMappingAs) formData.append('save_mapping_as', saveMappingAs);
    const params = new URLSearchParams();
    if (dryRun) params.set('dry_run', 'true');
    if (previewOnly) params.set('preview_only', 'true');
    if (allowDuplicates) params.set('allow_duplicates', 'true');
    const qs = params.toString();
    return api.post(`/students/import${qs ? '?' + qs : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importTemplateUrl: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/students/import/template.csv`,
  importTemplate: () => api.get('/students/import/template.csv', { responseType: 'blob' }),
  regularizeReturningPreview: () => api.get('/students/regularize-returning/preview'),
  regularizeReturning: () => api.post('/students/regularize-returning'),
  setEnrollmentStatus: (id, isNew) => api.put(`/students/${id}/enrollment-status`, { is_new: isNew }),
};

export const teachersImportApi = {
  import: (file, { dryRun = false, previewOnly = false, columnMapping = null, saveMappingAs = null } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (columnMapping) formData.append('column_mapping', JSON.stringify(columnMapping));
    if (saveMappingAs) formData.append('save_mapping_as', saveMappingAs);
    const params = new URLSearchParams();
    if (dryRun) params.set('dry_run', 'true');
    if (previewOnly) params.set('preview_only', 'true');
    const qs = params.toString();
    return api.post(`/teachers/import${qs ? '?' + qs : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  template: () => api.get('/teachers/import/template.csv', { responseType: 'blob' }),
};

export const staffImportApi = {
  import: (file, { staffType, dryRun = false, previewOnly = false, columnMapping = null, saveMappingAs = null } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (columnMapping) formData.append('column_mapping', JSON.stringify(columnMapping));
    if (saveMappingAs) formData.append('save_mapping_as', saveMappingAs);
    const params = new URLSearchParams();
    if (staffType) params.set('staff_type', staffType);
    if (dryRun) params.set('dry_run', 'true');
    if (previewOnly) params.set('preview_only', 'true');
    const qs = params.toString();
    return api.post(`/staff/import${qs ? '?' + qs : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  template: (staffType) => api.get(`/staff/import/template.csv?staff_type=${encodeURIComponent(staffType)}`, { responseType: 'blob' }),
};

export const importBatchesApi = {
  list: (limit = 50) => api.get(`/import-batches?limit=${limit}`),
  rollback: (batchId) => api.post(`/import-batches/${batchId}/rollback`),
};

export const userMappingsApi = {
  list: (kind) => api.get(`/user-mappings${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`),
  delete: (id) => api.delete(`/user-mappings/${id}`),
};

export const biometricApi = {
  registerChallenge: (kind, employeeId) => api.post('/biometric/register/challenge', { kind, employee_id: employeeId }),
  registerVerify: (challengeId, credential, label) => api.post('/biometric/register/verify', { challenge_id: challengeId, credential, label }),
  authChallenge: (kind = null, employeeId = null) => api.post('/biometric/authenticate/challenge', { kind, employee_id: employeeId }),
  authVerify: (challengeId, credential, punchType = 'auto') => api.post('/biometric/authenticate/verify', { challenge_id: challengeId, credential, punch_type: punchType }),
  listCredentials: (kind, employeeId) => api.get(`/staff/${kind}/${employeeId}/biometric-credentials`),
  deleteCredential: (credentialId) => api.delete(`/staff/biometric-credentials/${credentialId}`),
  listPunches: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.date) qs.set('date', params.date);
    if (params.employeeId) qs.set('employee_id', params.employeeId);
    if (params.kind) qs.set('kind', params.kind);
    if (params.limit) qs.set('limit', String(params.limit));
    const s = qs.toString();
    return api.get(`/staff/time-punches${s ? '?' + s : ''}`);
  },
  employeePunches: (kind, employeeId, days = 30) => api.get(`/staff/${kind}/${employeeId}/time-punches?days=${days}`),
  manualPunch: (kind, employeeId, type, timestamp = null) => api.post('/staff/time-punches/manual', { kind, employee_id: employeeId, type, timestamp }),
  workHours: ({ fromDate, toDate, kind } = {}) => {
    const qs = new URLSearchParams();
    if (fromDate) qs.set('from_date', fromDate);
    if (toDate) qs.set('to_date', toDate);
    if (kind) qs.set('kind', kind);
    const s = qs.toString();
    return api.get(`/staff/work-hours${s ? '?' + s : ''}`);
  },
  hrAlerts: (date) => api.get(`/staff/hr-alerts${date ? `?date=${date}` : ''}`),
  getPlanning: (kind, employeeId) => api.get(`/staff/${kind}/${employeeId}/planning`),
  setPlanning: (payload) => api.post('/staff/planning', payload),
  payslipPdf: (kind, employeeId, { fromDate, toDate } = {}) => {
    const qs = new URLSearchParams();
    if (fromDate) qs.set('from_date', fromDate);
    if (toDate) qs.set('to_date', toDate);
    const s = qs.toString();
    return api.get(`/staff/${kind}/${employeeId}/payslip.pdf${s ? '?' + s : ''}`, { responseType: 'blob' });
  },
  sendArrivalReminders: () => api.post('/staff/send-arrival-reminders'),
  // iter56 — Payslip history / bulk export / bank export
  listPayslips: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.kind) qs.set('kind', params.kind);
    if (params.employeeId) qs.set('employee_id', params.employeeId);
    if (params.fromDate) qs.set('from_date', params.fromDate);
    if (params.toDate) qs.set('to_date', params.toDate);
    if (params.signed !== undefined) qs.set('signed', String(params.signed));
    if (params.limit) qs.set('limit', String(params.limit));
    const s = qs.toString();
    return api.get(`/staff/payslips${s ? '?' + s : ''}`);
  },
  payslipMeta: (payslipId) => api.get(`/staff/payslips/${payslipId}`),
  payslipPdfById: (payslipId) => api.get(`/staff/payslips/${payslipId}/pdf`, { responseType: 'blob' }),
  generatePayslipsBulk: (payload) => api.post('/staff/payslips/generate-bulk', payload, { responseType: 'blob' }),
  bankExportCsv: (params) => {
    const qs = new URLSearchParams();
    qs.set('from_date', params.fromDate);
    qs.set('to_date', params.toDate);
    if (params.format) qs.set('format', params.format);
    if (params.kind) qs.set('kind', params.kind);
    return api.get(`/staff/bank-export.csv?${qs.toString()}`, { responseType: 'blob' });
  },
  listCompliance: (limit = 24) => api.get(`/staff/payroll-compliance?limit=${limit}`),
  complianceOverview: (months = 12, kind = null) => {
    const qs = new URLSearchParams({ months: String(months) });
    if (kind) qs.set('kind', kind);
    return api.get(`/staff/payroll-compliance/overview?${qs.toString()}`);
  },
  generateCompliance: (month = null, notify = false) => {
    const qs = new URLSearchParams();
    if (month) qs.set('month', month);
    if (notify) qs.set('notify', 'true');
    const s = qs.toString();
    return api.post(`/staff/payroll-compliance/generate${s ? '?' + s : ''}`);
  },
  complianceOverviewXlsxUrl: (months = 12, kind = null) => {
    const qs = new URLSearchParams({ months: String(months) });
    if (kind) qs.set('kind', kind);
    return `${API_URL}/api/staff/payroll-compliance/overview.xlsx?${qs.toString()}`;
  },
  complianceReportUrl: (key) =>
    `${API_URL}/api/staff/payroll-compliance/${key}.pdf`,
  sendPayslipSignLink: (payslipId, channels = ['email', 'sms']) =>
    api.post(`/staff/payslips/${payslipId}/send-link`, { channels }),
  // iter58 — Reminder campaign
  triggerPayslipReminders: () => api.post('/staff/payslips/send-reminders'),
  payslipCampaignStats: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.month) qs.set('month', params.month);
    if (params.kind) qs.set('kind', params.kind);
    const s = qs.toString();
    return api.get(`/staff/payslips/campaign-stats${s ? '?' + s : ''}`);
  },
};

// iter56 — Public payslip signature (no auth) — used from /sign-payslip/:token
export const publicPayslipApi = {
  meta: (token) => api.get(`/public/payslip/${token}`),
  pdfUrl: (token) => `${API_URL}/api/public/payslip/${token}/pdf`,
  sign: (token, payload) => api.post(`/public/payslip/${token}/sign`, payload),
};

// iter67/68 — Bulletin signatures + compliance
export const bulletinSignaturesApi = {
  createLink: (payload) => api.post('/bulletins/signature-link', payload),
  listLinks: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.className) qs.set('class_name', params.className);
    if (params.periodKey) qs.set('period_key', params.periodKey);
    if (params.schoolYear) qs.set('school_year', params.schoolYear);
    if (params.signed !== undefined) qs.set('signed', String(params.signed));
    if (params.limit) qs.set('limit', String(params.limit));
    const s = qs.toString();
    return api.get(`/bulletins/signature-links${s ? '?' + s : ''}`);
  },
  campaignStats: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.className) qs.set('class_name', params.className);
    if (params.periodKey) qs.set('period_key', params.periodKey);
    if (params.schoolYear) qs.set('school_year', params.schoolYear);
    const s = qs.toString();
    return api.get(`/bulletins/campaign-stats${s ? '?' + s : ''}`);
  },
  sendReminders: () => api.post('/bulletins/send-reminders'),
  sendLinkNow: (sigId) => api.post(`/bulletins/signature-link/${sigId}/send-link`),
  sendClassLinks: (payload) => api.post('/bulletins/send-class-links', payload),
  complianceOverview: (months = 12) =>
    api.get(`/bulletins/compliance-overview?months=${months}`),
  complianceOverviewXlsxUrl: (months = 12) =>
    `${API_URL}/api/bulletins/compliance-overview.xlsx?months=${months}`,
  generateCompliance: (month = null, notify = false) => {
    const qs = new URLSearchParams();
    if (month) qs.set('month', month);
    if (notify) qs.set('notify', 'true');
    const s = qs.toString();
    return api.post(`/bulletins/compliance/generate${s ? '?' + s : ''}`);
  },
  complianceReportUrl: (key) => `${API_URL}/api/bulletins/compliance/${key}.pdf`,
  slaAlertsCheck: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.months) qs.set('months', String(params.months));
    if (params.thresholdHours != null) qs.set('threshold_hours', String(params.thresholdHours));
    if (params.dryRun) qs.set('dry_run', 'true');
    const s = qs.toString();
    return api.post(`/bulletins/sla-alerts/check${s ? '?' + s : ''}`);
  },
  slaAlertsList: (limit = 50) => api.get(`/bulletins/sla-alerts?limit=${limit}`),
};


// Teachers
export const teachersApi = {
  getAll: () => api.get('/teachers'),
  create: (data) => api.post('/teachers', data),
  update: (id, data) => api.put(`/teachers/${id}`, data),
  delete: (id) => api.delete(`/teachers/${id}`),
  identityCardPdf: (id) => api.get(`/teachers/${id}/identity-card.pdf`, { responseType: 'blob' }),
  identityCardsBulkPdf: () => api.get(`/teachers/identity-cards.pdf`, { responseType: 'blob' }),
  exportXlsx: () => api.get(`/teachers/export.xlsx`, { responseType: 'blob' }),
  forClass: (className) => api.get(`/classes/${encodeURIComponent(className)}/teachers`),
};

// Staff
export const staffApi = {
  getAll: (params) => api.get('/staff', { params }),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  identityCardPdf: (id) => api.get(`/staff/${id}/identity-card.pdf`, { responseType: 'blob' }),
  identityCardsBulkPdf: (staffType) => api.get(`/staff/identity-cards.pdf`, { params: { staff_type: staffType }, responseType: 'blob' }),
  exportXlsx: (staffType) => api.get(`/staff/export.xlsx`, { params: staffType ? { staff_type: staffType } : {}, responseType: 'blob' }),
};

// Payments
export const paymentsApi = {
  getAll: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments', data),
  delete: (id) => api.delete(`/payments/${id}`),
  getReceipt: (id) => api.get(`/payments/${id}/receipt`, { responseType: 'blob' })
};

// Tuitions
export const tuitionsApi = {
  getAll: (params) => api.get('/tuitions', { params }),
  update: (studentId, data) => api.put(`/tuitions/${studentId}`, data)
};

// Expenses
export const expensesApi = {
  getAll: (params) => api.get('/expenses', { params }),
  create: (formData) => api.post('/expenses', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/expenses/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (id) => api.delete(`/expenses/${id}`),
  summary: (params) => api.get('/expenses/summary', { params }),
  reconciliation: (params) => api.get('/expenses/reconciliation', { params }),
  exportUrl: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/expenses/export.xlsx`,
  receiptUrl: (id) => `${process.env.REACT_APP_BACKEND_URL || ''}/api/expenses/${id}/receipt`,
};

// Role page permissions (fine-grained menu visibility per profile)
export const rolePermissionsApi = {
  getAll: () => api.get('/role-permissions'),
  getMine: () => api.get('/role-permissions/me'),
  set: (role, allowed_paths) => api.put('/role-permissions', { role, allowed_paths }),
  reset: (role) => api.delete(`/role-permissions/${role}`),
};

// Custom (admin-created) named profiles
export const customRolesApi = {
  list: () => api.get('/roles'),
  create: (payload) => api.post('/roles', payload),
  update: (code, payload) => api.put(`/roles/${code}`, payload),
  remove: (code) => api.delete(`/roles/${code}`),
};

// Access-denial log (blocked API calls per profile)
export const accessDenialsApi = {
  list: () => api.get('/access-denials'),
  count: () => api.get('/access-denials/count'),
  retention: () => api.get('/access-denials/retention'),
  setRetention: (days) => api.put('/access-denials/retention', { days }),
  export: () => api.get('/access-denials/export.xlsx', { responseType: 'blob' }),
  sendSummary: () => api.post('/access-denials/send-summary'),
  clear: () => api.delete('/access-denials'),
  dismiss: (id) => api.delete(`/access-denials/${id}`),
};

// Grant history (which page granted to which profile, when)
export const grantHistoryApi = {
  list: () => api.get('/grant-history'),
};

// Supplies
export const suppliesApi = {
  getAll: () => api.get('/supplies'),
  create: (data) => api.post('/supplies', data),
  update: (id, data) => api.put(`/supplies/${id}`, data)
};

// Attendance
export const attendanceApi = {
  getAll: (params) => api.get('/attendance', { params }),
  create: (data) => api.post('/attendance', data)
};

// Salaries
export const salariesApi = {
  getAll: (params) => api.get('/salaries', { params }),
  create: (data) => api.post('/salaries', data),
  update: (id, data) => api.put(`/salaries/${id}`, data),
  delete: (id) => api.delete(`/salaries/${id}`),
};

// Reports
export const reportsApi = {
  getFinancial: () => api.get('/reports/financial'),
  getForecast: () => api.get('/reports/forecast'),
  getCashflow: () => api.get('/reports/cashflow'),
  getDiscounts: (params) => api.get('/reports/discounts-summary', { params }),
  getPaymentMethods: (params) => api.get('/reports/payment-methods', { params }),
  getPaymentMethodsMonthly: (months = 12) => api.get('/reports/payment-methods/monthly', { params: { months } }),
  exportExcel: (type) => api.get(`/reports/export/excel?report_type=${type}`, { responseType: 'blob' }),
  exportDiscountsPdf: () => api.get('/reports/discounts-summary/export-pdf', { responseType: 'blob' }),
  exportPaymentMethodsPdf: (params) => api.get('/reports/payment-methods/export-pdf', { params, responseType: 'blob' }),
  previewMonthlyRecap: () => api.get('/admin/monthly-recap/preview-pdf', { responseType: 'blob' }),
  sendMonthlyRecap: (payload = {}) => api.post('/admin/monthly-recap/send-now', payload),
};

// Fee Settings
export const feeSettingsApi = {
  get: () => api.get('/fee-settings'),
  update: (data) => api.put('/fee-settings', data)
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getAlerts: (params) => api.get('/dashboard/alerts', { params: params || {} }),
  getSmsStats: (days = 30) => api.get('/dashboard/sms-stats', { params: { days } })
};

// Parent Portal
export const parentApi = {
  register: (data) => api.post('/parent/register', data),
  linkChild: (studentCode) => api.post('/parent/link-child', { student_code: studentCode }),
  getChildren: () => api.get('/parent/children'),
  getPayments: (studentId) => api.get('/parent/payments', { params: { student_id: studentId } }),
  getMessages: () => api.get('/parent/messages'),
  sendMessage: (data) => api.post('/parent/messages', data),
  getMessageDetail: (id) => api.get(`/parent/messages/${id}`),
  replyMessage: (id, data) => api.post(`/parent/messages/${id}/reply`, data)
  ,
  absences: (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.studentId) params.set('student_id', opts.studentId);
    if (opts.daysBack) params.set('days_back', String(opts.daysBack));
    const qs = params.toString();
    return api.get(`/parent/absences${qs ? `?${qs}` : ''}`);
  }
};

// Messages (Admin)
export const messagesApi = {
  getAll: (params) => api.get('/messages', { params }),
  broadcast: (data) => api.post('/messages/broadcast', data),
  getMessageDetail: (id) => api.get(`/parent/messages/${id}`),
  replyMessage: (id, data) => api.post(`/parent/messages/${id}/reply`, data)
};

// Student Code
export const studentCodeApi = {
  getCode: (studentId) => api.get(`/students/${studentId}/code`)
};

// School Configuration (logo, name, founder, ...)
export const schoolConfigApi = {
  get: () => api.get('/school/config'),
  update: (data) => api.put('/school/config', data),
  getPaymentSettings: () => api.get('/school/payment-settings'),
  updatePaymentSettings: (data) => api.put('/school/payment-settings', data),
};

// School Years
export const schoolYearsApi = {
  getAll: () => api.get('/school-years'),
  getActive: () => api.get('/school-years/active'),
  create: (data) => api.post('/school-years', data),
  update: (id, data) => api.put(`/school-years/${id}`, data),
  delete: (id) => api.delete(`/school-years/${id}`)
};

// Fee Configuration (registration / re-registration / transport zones)
export const feeConfigApi = {
  get: (schoolYear) => api.get('/fee-config', { params: schoolYear ? { school_year: schoolYear } : {} }),
  update: (data) => api.put('/fee-config', data),
  addTransportZone: (data) => api.post('/fee-config/transport-zones', data)
};

// Class Fee Configuration
export const classFeeConfigApi = {
  getAll: (schoolYear) => api.get('/class-fee-config', { params: schoolYear ? { school_year: schoolYear } : {} }),
  update: (className, data) => api.put(`/class-fee-config/${className}`, data)
};

// Teacher Availability
export const teacherAvailabilityApi = {
  get: (teacherId) => api.get(`/teacher-availability/${teacherId}`),
  set: (data) => api.post('/teacher-availability', data)
};

// Appointments
export const appointmentsApi = {
  getAll: (params) => api.get('/appointments', { params }),
  create: (data) => api.post('/appointments', data),
  updateStatus: (id, data) => api.put(`/appointments/${id}`, data),
  getAvailableSlots: (teacherId, date) => api.get('/appointments/available-slots', { params: { teacher_id: teacherId, date } }),
  getTeacherAvailability: (teacherId) => api.get(`/teacher-availability/${teacherId}`),
};

// Timetables
export const timetablesApi = {
  getAll: (params) => api.get('/timetables', { params }),
  create: (data) => api.post('/timetables', data),
  update: (id, data) => api.put(`/timetables/${id}`, data),
  delete: (id) => api.delete(`/timetables/${id}`),
  getParentTimetable: (studentId) => api.get('/parent/timetable', { params: studentId ? { student_id: studentId } : {} }),
  exportPdf: (className, schoolYear) => api.get('/timetables/pdf', { params: { class_name: className, ...(schoolYear ? { school_year: schoolYear } : {}) }, responseType: 'blob' }),
  exportParentPdf: (studentId) => api.get('/parent/timetable/pdf', { params: studentId ? { student_id: studentId } : {}, responseType: 'blob' })
};

// Cursus
export const cursusApi = {
  getByStudent: (studentId) => api.get(`/students/${studentId}/cursus`),
  timeline: (studentId) => api.get(`/students/${studentId}/timeline`),
  upsert: (studentId, data) => api.post(`/students/${studentId}/cursus`, data),
  delete: (entryId) => api.delete(`/cursus/${entryId}`),
  getParentCursus: (studentId) => api.get('/parent/cursus', { params: studentId ? { student_id: studentId } : {} }),
  byClass: (className, schoolYear) => api.get('/cursus/by-class', { params: { class_name: className, school_year: schoolYear } }),
  promoteClass: (data) => api.post('/cursus/promote-class', data)
};

// Bulletins (extended) — module v1 supprimé. La v2 vit dans gradesApi (computed/bulletinPdf/etc.).

// Notifications
export const notificationsApi = {
  status: () => api.get('/notifications/status'),
  test: (data) => api.post('/notifications/test', data),
  remindAppointments: (daysAhead) => api.post('/notifications/reminders/appointments', null, { params: daysAhead ? { days_ahead: daysAhead } : {} }),
  remindBalances: (data) => api.post('/notifications/reminders/balances', data || { threshold: 0 }),
  getLogs: (params) => api.get('/notifications/logs', { params: params || {} })
};

// Student fees / discounts (admin only)
export const studentFeesApi = {
  get: (studentId) => api.get(`/student-fees/${studentId}`),
  update: (studentId, data) => api.put(`/student-fees/${studentId}`, data),
  getDiscounts: (studentId) => api.get(`/student-fees/${studentId}/discounts`),
  updateDiscounts: (studentId, data) => api.put(`/student-fees/${studentId}/discounts`, data),
  toggleService: (studentId, payload) => api.post(`/student-fees/${studentId}/toggle-service`, payload),
  bulkEnableServices: (payload) => api.post('/student-fees/bulk-enable-services', payload),
  classSummary: (className) => api.get('/student-fees-class-summary', { params: { class_name: className } }),
  downloadCsvTemplate: () => api.get('/student-fees-csv/template', { responseType: 'blob' }),
  importServicesCsv: (formData) => api.post('/student-fees-csv/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Cantine (mobile companion)
export const cantineApi = {
  listMenus: (params) => api.get('/cantine/menus', { params: params || {} }),
  todayMenu: () => api.get('/cantine/menus/today'),
  upsertMenu: (data) => api.post('/cantine/menus', data),
  deleteMenu: (id) => api.delete(`/cantine/menus/${id}`),
  getAllergies: (studentId) => api.get(`/cantine/allergies/${studentId}`),
  setAllergies: (studentId, data) => api.put(`/cantine/allergies/${studentId}`, data),
  payments: (studentId) => api.get(`/cantine/payments/${studentId}`)
};

// Transport (mobile companion)
export const transportApi = {
  listRoutes: () => api.get('/transport/routes'),
  getRoute: (id) => api.get(`/transport/routes/${id}`),
  createRoute: (data) => api.post('/transport/routes', data),
  updateRoute: (id, data) => api.put(`/transport/routes/${id}`, data),
  deleteRoute: (id) => api.delete(`/transport/routes/${id}`),
  pushLocation: (data) => api.post('/transport/location', data),
  getLocation: (routeId) => api.get(`/transport/location/${routeId}`),
  payments: (studentId) => api.get(`/transport/payments/${studentId}`)
};

// Mobile staff PIN auth
export const staffPinApi = {
  setPin: (data) => api.post('/staff-pin/set', data),
  login: (data) => api.post('/staff-pin/login', data)
};

// Mobile TEACHER PIN auth (6-digit) + dedicated teacher endpoints
export const teacherPinApi = {
  setPin: (data) => api.post('/teacher-pin/set', data),
  login: (data) => api.post('/teacher-pin/login', data),
};

export const mobileTeacherApi = {
  me: () => api.get('/mobile/teacher/me'),
  timetable: () => api.get('/mobile/teacher/timetable'),
  appointments: (status) => api.get('/mobile/teacher/appointments', { params: status ? { status } : {} }),
  notificationsCount: () => api.get('/mobile/teacher/notifications-count'),
  availability: (teacherId) => api.get(`/teacher-availability/${teacherId}`),
  setAvailability: (data) => api.post('/teacher-availability', data),
  updateAppointment: (id, data) => api.put(`/appointments/${id}`, data),
};

// QR codes & check-in
export const qrApi = {
  studentPngUrl: (studentId) => `${API_URL}/api/students/${studentId}/qr.png`,
  sheetPdf: (className) => api.get('/students/qr-sheet.pdf', { params: className ? { class_name: className } : {}, responseType: 'blob' }),
  cantineCheckin: (qrToken) => api.post('/cantine/checkin', { qr_token: qrToken }),
  cantineAttendances: (day) => api.get('/cantine/attendances', { params: day ? { day } : {} }),
  transportCheckin: (qrToken, routeId, action) => api.post('/transport/checkin', { qr_token: qrToken, route_id: routeId, action }),
  transportAttendances: (routeId, day) => api.get(`/transport/attendances/${routeId}`, { params: day ? { day } : {} })
};

export const paymentsOnlineApi = {
  checkout: (studentId, feeCategory, originUrl) =>
    api.post('/payments-online/checkout', { student_id: studentId, fee_category: feeCategory, origin_url: originUrl }),
  status: (sessionId) => api.get(`/payments-online/status/${sessionId}`),
  listPending: () => api.get('/payments-online/pending'),
  reconcile: (sessionId) => api.post(`/payments-online/reconcile/${sessionId}`),
  // GeniusPay (Mobile Money / Cartes — Wave, Orange, MTN, Moov)
  geniuspayCheckout: (studentId, feeCategory, originUrl) =>
    api.post('/payments-online/geniuspay/checkout', { student_id: studentId, fee_category: feeCategory, origin_url: originUrl }),
  geniuspayStatus: (reference) => api.get(`/payments-online/geniuspay/${reference}`),
  geniuspayListPending: () => api.get('/payments-online/geniuspay/pending/list'),
  geniuspayReconcile: (reference) => api.post(`/payments-online/geniuspay/reconcile/${reference}`),
  geniuspayEnv: () => api.get('/payments-online/geniuspay/env'),
  geniuspayAnalytics: (days = 30) => api.get(`/payments-online/geniuspay/analytics?days=${days}`),
};

export const adminPerformanceApi = {
  get: () => api.get('/admin/performance'),
  rebuildIndexes: () => api.post('/admin/performance/rebuild-indexes'),
};

export const adminSecurityApi = {
  list: (params = {}) => api.get('/admin/security/events', { params }),
  listIpBlocks: () => api.get('/admin/security/ip-blocks'),
  unblockIp: (ip) => api.post('/admin/security/ip-blocks/unblock', { ip }),
};

export const gradingApi = {
  templates: () => api.get('/grading-templates'),
  cyclePeriodConfig: () => api.get('/grading/cycle-period-config'),
  saveCyclePeriodConfig: (cfg) => api.put('/grading/cycle-period-config', cfg),
  classConfigs: (schoolYear) => api.get('/grading/class-config', { params: schoolYear ? { school_year: schoolYear } : {} }),
  classConfig: (className, schoolYear) =>
    api.get('/grading/class-config', { params: {
      class_name: className,
      ...(schoolYear ? { school_year: schoolYear } : {}),
    } }).then(r => ({
      ...r,
      data: Array.isArray(r.data)
        ? (r.data.find(x => x.class_name === className) || null)
        : r.data,
    })),
  saveClassConfig: (payload) => api.put('/grading/class-config', payload),
  classCoefficients: (className, schoolYear) =>
    api.get('/grading/class-coefficients', { params: { class_name: className, school_year: schoolYear } }),
  saveClassCoefficients: (payload) => api.put('/grading/class-coefficients', payload),
};

export const gradesApi = {
  list: (params) => api.get('/grades', { params }),
  bulkUpsert: (payload) => api.post('/grades/bulk-upsert', payload),
  computed: (className, periodKey, schoolYear) =>
    api.get('/grades/computed', { params: {
      class_name: className, period_key: periodKey,
      ...(schoolYear ? { school_year: schoolYear } : {}),
    } }),
  delete: (gradeId) => api.delete(`/grades/${gradeId}`),
  progression: (className, studentId, schoolYear) =>
    api.get('/grades/progression', { params: {
      class_name: className, student_id: studentId,
      ...(schoolYear ? { school_year: schoolYear } : {}),
    } }),
  availablePeriods: (studentId) => api.get(`/bulletins/v2/${studentId}/available-periods`),
  bulletinPdf: (studentId, className, periodKey, schoolYear) =>
    api.get(`/bulletins/v2/${studentId}/pdf`, {
      params: { class_name: className, period_key: periodKey, ...(schoolYear ? { school_year: schoolYear } : {}) },
      responseType: 'blob',
    }),
  bulletinsClassPdf: (className, periodKey, schoolYear) =>
    api.get(`/bulletins/v2/class/${className}/pdf`, {
      params: { period_key: periodKey, ...(schoolYear ? { school_year: schoolYear } : {}) },
      responseType: 'blob',
    }),
  lockPeriod: (payload) => api.post('/grading/period-locks', payload),
  unlockPeriod: (params) => api.delete('/grading/period-locks', { params }),
  annual: (className, schoolYear) =>
    api.get('/grades/annual', { params: {
      class_name: className,
      ...(schoolYear ? { school_year: schoolYear } : {}),
    } }),
};

// Class sections & teacher assignments (settings)
export const sectionsApi = {
  list: (schoolYear) => api.get('/class-sections', { params: schoolYear ? { school_year: schoolYear } : {} }),
  save: (data) => api.put('/class-sections', data),
  apply: (schoolYear) => api.post('/class-sections/apply', null, { params: schoolYear ? { school_year: schoolYear } : {} }),
  pedagogicalDistributionPdf: (schoolYear) =>
    api.get('/pedagogical-distribution.pdf', {
      params: schoolYear ? { school_year: schoolYear } : {},
      responseType: 'blob',
    }),
  teacherWorkloadPdf: (schoolYear) =>
    api.get('/teacher-workload.pdf', {
      params: schoolYear ? { school_year: schoolYear } : {},
      responseType: 'blob',
    }),
  teacherWorkloadAlerts: (schoolYear) =>
    api.get('/teacher-workload/alerts', {
      params: schoolYear ? { school_year: schoolYear } : {},
    }),
  backToSchoolChecklist: (schoolYear) =>
    api.get('/back-to-school/checklist', {
      params: schoolYear ? { school_year: schoolYear } : {},
    }),
};

export const classTeachersApi = {
  list: (className) => api.get(`/class-teachers/${encodeURIComponent(className)}`),
  save: (data) => api.put('/class-teachers', data),
  remove: (className, subject) => api.delete('/class-teachers', { params: { class_name: className, subject } }),
};

// Admin notifications dashboard
export const notifLogsApi = {
  list: (params) => api.get('/admin/notification-logs', { params }),
  stats: (days) => api.get('/admin/notification-logs/stats', { params: { days } }),
  prune: (olderThanDays) => api.delete('/admin/notification-logs', { params: { older_than_days: olderThanDays } }),
};

export const yearEndApi = {
  summary: (schoolYear) => api.get('/year-end/summary', { params: schoolYear ? { school_year: schoolYear } : {} }),
  classView: (className, schoolYear) => api.get(`/year-end/class/${encodeURIComponent(className)}`, { params: schoolYear ? { school_year: schoolYear } : {} }),
  setStatuses: (payload) => api.put('/year-end/status', payload),
  preview: (schoolYear) => api.post('/year-end/preview', null, { params: schoolYear ? { school_year: schoolYear } : {} }),
  execute: (schoolYear) => api.post('/year-end/execute', null, { params: schoolYear ? { school_year: schoolYear } : {} }),
  rollback: (schoolYear) => api.post('/year-end/rollback', null, { params: schoolYear ? { school_year: schoolYear } : {} }),
  reassign: (studentId, newClass) => api.post('/year-end/reassign', null, { params: { student_id: studentId, new_class: newClass } }),
  archived: () => api.get('/year-end/archived'),
  rostersPdf: (schoolYear) => api.get('/year-end/rosters.pdf', { params: schoolYear ? { school_year: schoolYear } : {}, responseType: 'blob' }),
  rostersXlsx: (schoolYear) => api.get('/year-end/rosters.xlsx', { params: schoolYear ? { school_year: schoolYear } : {}, responseType: 'blob' }),
};

export const pushApi = {
  vapidKey: () => api.get('/push/vapid-public-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
  test: () => api.post('/push/test', {}),
  status: () => api.get('/push/status'),
  adminStats: () => api.get('/admin/push/stats'),
  adminBroadcast: (payload) => api.post('/admin/push/broadcast', payload),
  adminHistory: (opts = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(opts.limit ?? 50));
    if (opts.since) params.set('since', opts.since);
    if (opts.until) params.set('until', opts.until);
    if (opts.role) params.set('role', opts.role);
    return api.get(`/admin/push/history?${params.toString()}`);
  },
  adminResend: (logId) => api.post(`/admin/push/history/${logId}/resend`),
  adminDeleteLog: (logId) => api.delete(`/admin/push/history/${logId}`),
};

// ============================================================
// iter39 — Student attendance ("Présences élèves")
// ============================================================
export const studentAttendanceApi = {
  getClass: (className, date, slotId) => {
    const params = new URLSearchParams({ date });
    if (slotId) params.set('slot_id', slotId);
    return api.get(`/student-attendance/class/${encodeURIComponent(className)}?${params.toString()}`);
  },
  saveRollcall: (payload) => api.post('/student-attendance/rollcall', payload),
  teacherSlots: (date) => api.get(`/student-attendance/teacher/slots?date=${encodeURIComponent(date)}`),
  // iter40 — justifications
  pendingJustifications: (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.className) params.set('class_name', opts.className);
    if (opts.daysBack) params.set('days_back', String(opts.daysBack));
    const qs = params.toString();
    return api.get(`/student-attendance/pending-justifications${qs ? `?${qs}` : ''}`);
  },
  justify: (attendanceId, payload) => api.post(`/student-attendance/${attendanceId}/justify`, payload),
  justifyBulk: (payload) => api.post('/student-attendance/justify-bulk', payload),
  studentDossier: (studentId, days = 90) => api.get(`/student-attendance/student/${studentId}/dossier?days=${days}`),
  journal: (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.studentId) params.set('student_id', opts.studentId);
    if (opts.daysBack) params.set('days_back', String(opts.daysBack));
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.className) params.set('class_name', opts.className);
    if (opts.byUserId) params.set('by_user_id', opts.byUserId);
    if (opts.byRole) params.set('by_role', opts.byRole);
    if (opts.fromDate) params.set('from_date', opts.fromDate);
    if (opts.toDate) params.set('to_date', opts.toDate);
    if (opts.statusFilter) params.set('status_filter', opts.statusFilter);
    if (opts.includeAll) params.set('include_all', 'true');
    const qs = params.toString();
    return api.get(`/student-attendance/journal${qs ? `?${qs}` : ''}`);
  },
  journalFilters: (daysBack = 90) => api.get(`/student-attendance/journal/filters?days_back=${daysBack}`),
  educatorNotesList: (studentId) => api.get(`/student-attendance/student/${studentId}/educator-notes`),
  educatorNoteAdd: (studentId, text) => api.post(`/student-attendance/student/${studentId}/educator-notes`, { text }),
  educatorNoteDelete: (noteId) => api.delete(`/student-attendance/educator-notes/${noteId}`),
  convocationPdf: (studentId, payload) => api.post(
    `/student-attendance/student/${studentId}/convocation-pdf`,
    payload,
    { responseType: 'blob' },
  ),
  convocationMotifTemplates: () => api.get('/student-attendance/convocation-motif-templates'),
  justificationPdf: (attendanceId) => api.get(
    `/student-attendance/${attendanceId}/justification-pdf`,
    { responseType: 'blob' },
  ),
  studentConvocations: (studentId, limit = 50) => api.get(
    `/student-attendance/student/${studentId}/convocations?limit=${limit}`,
  ),
  convocationPdfById: (convId) => api.get(
    `/student-attendance/convocations/${convId}/pdf`,
    { responseType: 'blob' },
  ),
  stats: (window, className) => {
    const params = new URLSearchParams({ window: String(window) });
    if (className) params.set('class_name', className);
    return api.get(`/student-attendance/stats?${params.toString()}`);
  },
  exportCsvUrl: (window, className) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const params = new URLSearchParams({ window: String(window) });
    if (className) params.set('class_name', className);
    return `${base}/api/student-attendance/export.csv?${params.toString()}`;
  },
  directorAlertsFeed: (days = 7) => api.get(`/director/alerts-feed?days=${days}`),
  parentContacts: (studentId) => api.get(`/director/student/${studentId}/parent-contacts`),
};


export default api;
